import { Link } from '@tanstack/react-router'
import { ArrowRight, Sparkles } from 'lucide-react'
import { Button } from '@/ui'
import { cn, useTranslation } from '@/core'

interface HeroSectionProps {
  className?: string
  showCTA?: boolean
  /**
   * `authPageProps` is rendered by the router `Wrap`, where TanStack's router
   * context is not available yet. Use a native link in that boundary.
   */
  ctaLinkMode?: 'router' | 'anchor'
}

export function HeroSection({
  className,
  showCTA = true,
  ctaLinkMode = 'router',
}: HeroSectionProps) {
  const { t } = useTranslation()

  return (
    <section
      className={cn(
        'relative overflow-hidden bg-linear-to-br from-blue-600 via-blue-500 to-indigo-600 py-20 sm:py-28 lg:py-32',
        className,
      )}
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -right-40 -top-40 size-80 bg-blue-400/20 rounded-full blur-3xl" />
        <div className="absolute -left-40 -bottom-40 size-80 bg-indigo-400/20 rounded-full blur-3xl" />
      </div>

      {/* Content */}
      <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
        {/* Badge */}
        <div className="mb-6 flex items-center justify-center gap-2">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-1.5 backdrop-blur-sm border border-white/30">
            <Sparkles className="size-4 text-white" />
            <span className="text-sm font-semibold text-white">
              {t('landing.badge')}
            </span>
          </div>
        </div>

        {/* Title */}
        <h1 className="mb-4 text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight">
          {t('app.title')}
        </h1>

        {/* Subtitle */}
        <p className="mx-auto mb-8 max-w-2xl text-lg sm:text-xl text-blue-100">
          {t('app.description')}
        </p>

        {/* Secondary description */}
        <p className="mb-8 text-base text-blue-100 opacity-90">
          {t('landing.heroSubtitle')}
        </p>

        {/* CTA Buttons */}
        {showCTA && (
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {ctaLinkMode === 'router' ? (
              <Button
                asChild
                size="lg"
                className="bg-white text-blue-600 hover:bg-gray-100 shadow-lg hover:shadow-xl transition-all"
              >
                <Link to="/login">
                  {t('dashboard.accessSystem')}
                  <ArrowRight className="ml-2 size-5" />
                </Link>
              </Button>
            ) : (
              <Button
                asChild
                size="lg"
                className="bg-white text-blue-600 hover:bg-gray-100 shadow-lg hover:shadow-xl transition-all"
              >
                <a href="/login">
                  {t('dashboard.accessSystem')}
                  <ArrowRight className="ml-2 size-5" />
                </a>
              </Button>
            )}

            <Button variant="outline" size="lg" className="" asChild>
              <a href="#features">{t('landing.learnMore')}</a>
            </Button>
          </div>
        )}
      </div>
    </section>
  )
}
